import React from "react";
import { motion } from "motion/react";
import { 
  Code2, 
  Database, 
  LayoutDashboard, 
  Palette,
  Bot,
  Server
} from "lucide-react";

const skillCategories = [
  {
    title: "Full-stack Core",
    icon: <Code2 className="text-violet-400" size={32} />,
    skills: ["JavaScript", "TypeScript", "React", "Next.js", "Node.js", "PHP"],
    description: "Building modern application structures with high-performance frontend and backend logic.",
    color: "from-violet-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(139,92,246,0.2)]",
    border: "group-hover:border-violet-500/50"
  },
  {
    title: "Data & Architecture",
    icon: <Database className="text-cyan-400" size={32} />,
    skills: ["MySQL", "PostgreSQL", "SQL / NoSQL", "Database Design", "API Integration"],
    description: "Designing reliable backend logic, structured APIs, and complex data flows.",
    color: "from-cyan-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(6,182,212,0.2)]",
    border: "group-hover:border-cyan-500/50"
  },
  {
    title: "Business Solutions",
    icon: <LayoutDashboard className="text-fuchsia-400" size={32} />,
    skills: ["Admin Dashboards", "Booking Systems", "Reservation Flow", "i18n (Multilingual)"],
    description: "Developing custom tools for service and commercial businesses with specialized features.",
    color: "from-fuchsia-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(232,121,249,0.2)]",
    border: "group-hover:border-fuchsia-500/50"
  },
  {
    title: "UI/UX & Prototyping",
    icon: <Palette className="text-pink-400" size={32} />,
    skills: ["Figma", "Responsive Web Design", "Layout Implementation", "Asset Prep"],
    description: "Translating requirements into clean UI and memorable digital experiences.",
    color: "from-pink-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(236,72,153,0.2)]",
    border: "group-hover:border-pink-500/50"
  },
  {
    title: "Deployment & Ops",
    icon: <Server className="text-emerald-400" size={32} />,
    skills: ["Domain/DNS", "SSL / Security", "Hosting Config", "Vercel / DigitalOcean"],
    description: "Deploying and supporting projects end-to-end with secure, scalable infrastructures.",
    color: "from-emerald-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(16,185,129,0.2)]",
    border: "group-hover:border-emerald-500/50"
  },
  {
    title: "AI & Automation",
    icon: <Bot className="text-orange-400" size={32} />,
    skills: ["Prompt Engineering", "AI Platforms", "Workflow Automation", "Bot Integration"],
    description: "Leveraging AI for rapid iteration, prototyping, and automating repetitive tasks.",
    color: "from-orange-500/20 to-transparent",
    glow: "group-hover:shadow-[0_0_50px_rgba(249,115,22,0.2)]",
    border: "group-hover:border-orange-500/50"
  },
];

export function Skills() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {skillCategories.map((category, index) => (
        <motion.div
          key={category.title}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className={`relative p-10 bg-neutral-900/40 backdrop-blur-md border border-neutral-800/50 rounded-[2.5rem] transition-all duration-500 group overflow-hidden ${category.glow} ${category.border}`}
        >
          {/* Background Gradient Glow */}
          <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
          
          <div className="relative z-10">
            <div className="mb-8 p-5 bg-neutral-950/80 border border-neutral-800 rounded-2xl w-fit group-hover:scale-110 group-hover:border-white/20 transition-all duration-500">
              {category.icon}
            </div>
            <h3 className="text-2xl font-black mb-4 group-hover:text-white transition-colors uppercase tracking-tight">
              {category.title}
            </h3>
            <p className="text-neutral-500 text-sm md:text-base mb-8 leading-relaxed min-h-[70px] group-hover:text-neutral-300 transition-colors">
              {category.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {category.skills.map((skill) => (
                <span 
                  key={skill} 
                  className="text-[10px] px-3 py-1.5 bg-neutral-950/80 border border-neutral-800/50 rounded-xl text-neutral-400 font-black uppercase tracking-widest hover:border-white/30 hover:text-white transition-all cursor-default"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}